import { getApp, initializeApp } from 'firebase/app'
import {
	getAuth,
	getIdToken,
	onAuthStateChanged,
	type User,
} from 'firebase/auth'

import { getAppEnv } from '../utils/get-app-env'
import { getFirebaseConfig } from './config'
import type { AppEnv } from '../types/app-env'

export const getAppInstance = (env: AppEnv) => {
	const config = getFirebaseConfig(env)

	try {
		return getApp()
	} catch {
		return initializeApp(config)
	}
}

export const getAuthInstance = (env: AppEnv) => getAuth(getAppInstance(env))

const AUTH_STATE_TIMEOUT_MS = 5_000

const waitForAuthUser = (env: AppEnv): Promise<User | null> => {
	const auth = getAuthInstance(env)

	if (auth.currentUser) {
		return Promise.resolve(auth.currentUser)
	}

	return new Promise((resolve) => {
		let settled = false

		const finish = (user: User | null) => {
			if (settled) return
			settled = true
			resolve(user)
		}

		try {
			const unsub = onAuthStateChanged(auth, (user) => {
				unsub()
				finish(user)
			})

			setTimeout(() => {
				if (settled) return
				unsub()
				finish(auth.currentUser)
			}, AUTH_STATE_TIMEOUT_MS)
		} catch {
			finish(null)
		}
	})
}

export const getUserToken = async (
	env: AppEnv = getAppEnv(),
): Promise<string | undefined> => {
	try {
		const user = await waitForAuthUser(env)
		if (!user) return undefined

		return await Promise.race([
			getIdToken(user),
			new Promise<undefined>((resolve) => {
				setTimeout(() => resolve(undefined), AUTH_STATE_TIMEOUT_MS)
			}),
		])
	} catch {
		return undefined
	}
}

export const getUser = async (
	env: AppEnv = getAppEnv(),
): Promise<User | undefined> => {
	try {
		const user = await waitForAuthUser(env)
		return user ?? undefined
	} catch {
		return undefined
	}
}
